import { Controller } from "@hotwired/stimulus";

// Handles reverse infinite scrolling for the posts index feed using Turbo streams only.
export default class extends Controller {
  static targets = ["sentinel"];
  static values = { busy: Boolean };

  initialize() {
    this.hasLoadedOnce = false;
    this.headerShownAfterInitialLoad = false;
  }

  connect() {
    this.setupObserver();
    // Add the posts-index-page-body class to the body for scroll behavior
    document.body.classList.add("posts-index-page-body");
    document.body.classList.add("scrolling-down");
    
    // Initialize scroll direction detection
    this.initializeScrollDetection();
  }

  disconnect() {
    this.teardownObserver();
    // Clean up scroll detection
    this.cleanupScrollDetection();
  }

  sentinelTargetConnected(element) {
    this.setupObserver(element);
  }

  sentinelTargetDisconnected() {
    this.teardownObserver();
  }

  setupObserver(element) {
    const sentinel = element || (this.hasSentinelTarget ? this.sentinelTarget : null);

    if (!sentinel) {
      this.teardownObserver();
      return;
    }

    this.teardownObserver();

    if (!('primed' in sentinel.dataset)) {
      sentinel.dataset.primed = this.hasLoadedOnce ? 'true' : 'false';
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        this.loadMore(entry.target);
      });
    }, { threshold: 0.05 });

    this.observer.observe(sentinel);

      // If sentinel is at the top and visible, trigger loadMore immediately
      const rect = sentinel.getBoundingClientRect();
      if (rect.top >= 0 && rect.bottom <= window.innerHeight && sentinel.dataset.primed !== 'true') {
        this.loadMore(sentinel);
      }
  }

  teardownObserver() {
    if (!this.observer) {
      return;
    }

    this.observer.disconnect();
    this.observer = null;
  }

  loadMore(element) {
    if (this.busyValue) {
      return;
    }

    if (element.dataset.primed !== 'true') {
      element.dataset.primed = 'true';

      if (this.hasLoadedOnce) {
        requestAnimationFrame(() => this.loadMore(element));
      }

      return;
    }

    const loadUrl = element.dataset.loadUrl;
    if (!loadUrl) {
      this.teardownObserver();
      return;
    }

    this.busyValue = true;

    const anchor = this.visibleAnchor();

    fetch(loadUrl, { headers: { Accept: "text/vnd.turbo-stream.html" } })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Turbo stream request failed with status ${response.status}`);
        }
        return response.text();
      })
      .then((html) => {
        if (!html.trim()) {
          return;
        }

        if (window.Turbo && typeof window.Turbo.renderStreamMessage === "function") {
          window.Turbo.renderStreamMessage(html);

          if (anchor) {
            requestAnimationFrame(() => {
              const anchorElement = document.getElementById(anchor.id);
              if (!anchorElement) {
                return;
              }

              const newTop = anchorElement.getBoundingClientRect().top;
              const delta = newTop - anchor.top;
              if (delta !== 0) {
                window.scrollBy(0, delta);
                // Scroll up by 1px to trigger header visibility after content load
                requestAnimationFrame(() => {
                  window.scrollBy(0, -1);
                });
              }
            });
          }
        }

        this.hasLoadedOnce = true;
      })
      .catch(() => {
        // Swallow errors to avoid breaking the page; observer will retry when possible.
      })
      .finally(() => {
        this.busyValue = false;
        requestAnimationFrame(() => this.setupObserver());
      });
  }

  visibleAnchor() {
    const dayElements = this.element.querySelectorAll('[id^="day-"]');
    const viewportHeight = window.innerHeight;

    for (const day of dayElements) {
      const rect = day.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < viewportHeight) {
        // To make sure that the day is loaded below the main header
        return { id: day.id, top: rect.top + 205 };
      }
    }

    return null;
  }

  initializeScrollDetection() {
    this.lastScrollTop = 0;
    this.scrollHandler = this.handleScroll.bind(this);
    window.addEventListener("scroll", this.scrollHandler);
  }

  cleanupScrollDetection() {
    if (this.scrollHandler) {
      window.removeEventListener("scroll", this.scrollHandler);
      this.scrollHandler = null;
    }
  }

  handleScroll() {
    let currentScroll = window.scrollY || document.documentElement.scrollTop;

    if (currentScroll > this.lastScrollTop && this.headerShownAfterInitialLoad) {
      // Scrolling down
      document.body.classList.remove("scrolling-down");
    } else {
      // Scrolling up
      document.body.classList.add("scrolling-down");
    }
    this.headerShownAfterInitialLoad = true;
    this.lastScrollTop = currentScroll <= 0 ? 0 : currentScroll; // Prevent negative values
  }
}
